/**
 * InteractiveMap.jsx — Google Maps JavaScript SDK wrapper for the AIChat
 * right panel. Replaces the Static Maps PNG approach (LG feedback
 * 2026-05-06: static map "looks artificial" because it can't pan/zoom).
 *
 * Path B v2 (2026-05-06 evening). The static-map version is kept as a
 * fallback for when the JS SDK can't load — see AIChat.jsx.
 *
 * Why a separate component:
 *   - Isolates the imperative SDK lifecycle (script load, geocode, marker
 *     create/destroy, bounds-fit) from AIChat's render-driven flow
 *   - Easy to delete or A/B-toggle from AIChat without touching anything
 *     else if we ever decide static is preferable
 *
 * Why vanilla rather than @react-google-maps/api:
 *   - One more dependency to maintain, and our use case is narrow enough
 *     (one map, one marker layer) that the wrapper is ~150 lines of
 *     straightforward effects
 *
 * SDK loading strategy:
 *   - Single module-level promise so concurrent mounts don't trigger
 *     duplicate <script> injections
 *   - Async-loaded with `loading=async` per Google's recommended pattern
 *   - On error, sdkLoadingPromise resets to null so a future mount can
 *     retry (e.g., if the user toggles offline → online)
 *
 * Marker styling:
 *   - Candidate pins: mid-indigo (#7F77DD) circles, no label
 *   - Workplace pin:  dark indigo (#1F1A4A) larger circle with "W" label
 *
 * Caller contract:
 *   - cityQuery     "Austin, TX" — used as initial geocode to center the
 *                   map before any markers exist
 *   - candidates    [{ name, lat, lng }] — neighborhood pins
 *   - workplaceCoords  { lat, lng } | null — workplace pin (when set)
 *   - workplaceLabel   string — used for the marker tooltip
 *   - onLoadError      () => void — called once if SDK fails to load,
 *                       so parent can render a fallback (static map)
 */

import React, { useEffect, useRef, useState } from 'react';

// Module-level singleton: the SDK should load exactly once across the
// app's lifetime. A second mount of InteractiveMap reuses this promise.
let sdkLoadingPromise = null;

function loadGoogleMapsSDK(apiKey) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window not available'));
  }
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }
  if (sdkLoadingPromise) return sdkLoadingPromise;

  // 2026-05-06 evening v2 fix: dropped `loading=async` + the
  // importLibrary() dance. The legacy `google.maps.Map`, `Marker`, and
  // `Geocoder` classes are part of the SDK core and don't need separate
  // library imports. With plain (non-async) script loading, `script.onload`
  // fires after the entire SDK is fully initialized, so all three classes
  // are immediately usable as constructors. The async pattern was meant
  // for the new AdvancedMarkerElement API; we're using legacy markers,
  // so async loading just added complexity without benefit.
  sdkLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps-sdk]');
    if (existing) {
      // Another loader started before us — just listen for its events.
      // If it's already resolved, the load event has fired and we can
      // proceed immediately.
      if (window.google && window.google.maps && window.google.maps.Map) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', (e) => {
        sdkLoadingPromise = null;
        reject(e);
      });
      return;
    }

    const script = document.createElement('script');
    // 2026-05-06 evening v3 (LG feedback "make pins better"): added
    // &libraries=marker so AdvancedMarkerElement + PinElement load
    // at SDK init time — these give the LITERAL native Google Maps
    // pin shape (the same component Google uses in their own apps),
    // not an SVG approximation. With libraries= specified upfront
    // they're synchronously available after script.onload, so we
    // don't need importLibrary or loading=async.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&libraries=marker`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsSdk = '1';
    script.onload = () => {
      // Sanity check: confirm the constructors are actually present.
      // We need both the core Map class AND the marker library
      // (AdvancedMarkerElement + PinElement). If either is missing,
      // fail explicitly so the static-map fallback can take over.
      if (!window.google ||
          !window.google.maps ||
          !window.google.maps.Map ||
          !window.google.maps.marker ||
          !window.google.maps.marker.AdvancedMarkerElement ||
          !window.google.maps.marker.PinElement) {
        sdkLoadingPromise = null;
        reject(new Error(
          'Maps SDK loaded but Map/AdvancedMarkerElement/PinElement is missing'
        ));
        return;
      }
      resolve();
    };
    script.onerror = (e) => {
      sdkLoadingPromise = null; // allow retry on next mount
      reject(e);
    };
    document.head.appendChild(script);
  });

  return sdkLoadingPromise;
}

/**
 * Convenience helper for the parent (AIChat.jsx) to decide whether to
 * try rendering an interactive map at all, before paying any of the
 * SDK-load cost. Strictly env-var driven.
 */
export function isInteractiveMapAvailable() {
  return Boolean(process.env.REACT_APP_GOOGLE_MAPS_KEY);
}

export default function InteractiveMap({
  cityQuery,
  candidates = [],
  workplaceCoords,
  workplaceLabel,
  onLoadError,
  className,
}) {
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const lastGeocodedCityRef = useRef(null);

  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  // ── 1) Load the SDK ───────────────────────────────────────────────────
  useEffect(() => {
    if (!apiKey) {
      setStatus('error');
      onLoadError && onLoadError();
      return;
    }
    let cancelled = false;
    loadGoogleMapsSDK(apiKey)
      .then(() => { if (!cancelled) setStatus('ready'); })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
        onLoadError && onLoadError();
      });
    return () => { cancelled = true; };
  }, [apiKey, onLoadError]);

  // ── 2) Initialize the map once SDK + container are both ready ───────
  useEffect(() => {
    if (status !== 'ready' || !containerRef.current || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(containerRef.current, {
      // Generic US-center placeholder; we re-center via geocode below.
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      // Strip the controls users don't need in this small embed; keep
      // pan + zoom so the "you can interact with it" feel is preserved
      // (the whole reason we migrated off Static Maps).
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: 'cooperative', // requires Ctrl+scroll to zoom (no scroll hijack)
      clickableIcons: false, // disable POI-click popups — they steal focus from chat
      // 2026-05-06 evening v3: mapId required for AdvancedMarkerElement
      // to render with full fidelity. DEMO_MAP_ID is Google's free
      // development map style; for production you can create a custom
      // styled map ID in Cloud Console (Maps Platform → Map Styles).
      // Without a mapId AdvancedMarkerElement still works but renders
      // a generic fallback instead of the iconic Google pin shape.
      mapId: 'DEMO_MAP_ID',
    });
    mapInstanceRef.current = map;
  }, [status]);

  // ── 3) Re-center on city when it changes (or when map first loads) ──
  // Geocoding is free up to a generous quota, but we still cache the
  // last-geocoded city in a ref to avoid re-geocoding the same city
  // every time a candidate prop changes.
  useEffect(() => {
    if (status !== 'ready' || !mapInstanceRef.current || !cityQuery) return;
    if (lastGeocodedCityRef.current === cityQuery) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: cityQuery }, (results, statusGeo) => {
      if (statusGeo === 'OK' && results[0] && mapInstanceRef.current) {
        mapInstanceRef.current.setCenter(results[0].geometry.location);
        // Only set zoom if we haven't dropped any pins yet — once pins
        // exist, the markers effect below owns the viewport via fitBounds.
        if (markersRef.current.length === 0) {
          mapInstanceRef.current.setZoom(11);
        }
        lastGeocodedCityRef.current = cityQuery;
      }
    });
  }, [status, cityQuery]);

  // ── 4) Create/update markers when candidates or workplace change ────
  useEffect(() => {
    if (status !== 'ready' || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    // 2026-05-06 evening v3 (LG feedback "make pins better"): switched
    // from legacy `google.maps.Marker` + custom SVG path to
    // `AdvancedMarkerElement` + `PinElement`. PinElement is Google's
    // first-party native pin — literally the same component Google
    // Maps uses in their own apps. It includes the iconic shape,
    // built-in shadow, and proper anchoring out of the box. Compared
    // to the previous SVG approximation, this is the difference
    // between "kinda looks like a Google pin" and "IS a Google pin."
    //
    // Cleanup: AdvancedMarkerElement detaches via `marker.map = null`
    // (legacy Marker used `setMap(null)`). The check below handles
    // both APIs in case of any mid-flight legacy markers from a
    // hot-reload scenario.
    markersRef.current.forEach((m) => {
      if (typeof m.setMap === 'function') {
        m.setMap(null);
      } else {
        m.map = null;
      }
    });
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let markerCount = 0;
    const { AdvancedMarkerElement, PinElement } = window.google.maps.marker;

    // Candidate markers — native Google pins in brand indigo.
    // PinElement params:
    //   background:   the bulb fill color
    //   borderColor:  the outer ring around the bulb (white reads
    //                 strongly against any map background)
    //   glyphColor:   color of the small dot inside the bulb (Google's
    //                 native "hole" — white matches the default pin)
    for (const c of candidates) {
      if (typeof c?.lat !== 'number' || typeof c?.lng !== 'number') continue;
      const pinView = new PinElement({
        background: '#4F46E5',
        borderColor: '#FFFFFF',
        glyphColor: '#FFFFFF',
        scale: 1.0,
      });
      const marker = new AdvancedMarkerElement({
        position: { lat: c.lat, lng: c.lng },
        map,
        title: c.name,
        content: pinView.element,
      });
      markersRef.current.push(marker);
      bounds.extend({ lat: c.lat, lng: c.lng });
      markerCount += 1;
    }

    // Workplace marker — same native pin shape, darker color, larger
    // scale, and a "W" glyph inside the bulb. zIndex floats it above
    // any candidate pin it might overlap.
    if (workplaceCoords &&
        typeof workplaceCoords.lat === 'number' &&
        typeof workplaceCoords.lng === 'number') {
      const pinView = new PinElement({
        background: '#0F0A2C',
        borderColor: '#FFFFFF',
        glyphColor: '#FFFFFF',
        glyph: 'W',
        scale: 1.3,
      });
      const marker = new AdvancedMarkerElement({
        position: { lat: workplaceCoords.lat, lng: workplaceCoords.lng },
        map,
        title: workplaceLabel || 'Workplace',
        content: pinView.element,
        zIndex: 100,
      });
      markersRef.current.push(marker);
      bounds.extend({ lat: workplaceCoords.lat, lng: workplaceCoords.lng });
      markerCount += 1;
    }

    // Frame the markers. With 2+ markers, fitBounds with padding gives
    // breathing room. With exactly 1 marker, fitBounds zooms way too far
    // in — better to setCenter + setZoom for a comfortable city-view.
    if (markerCount >= 2) {
      map.fitBounds(bounds, { top: 44, right: 44, bottom: 44, left: 44 });
    } else if (markerCount === 1) {
      map.setCenter(markersRef.current[0].getPosition());
      map.setZoom(13);
    }
  }, [status, candidates, workplaceCoords, workplaceLabel]);

  // ── 5) Cleanup on unmount ───────────────────────────────────────────
  useEffect(() => {
    return () => {
      // Same dual-API tear-down as the markers effect — handles both
      // legacy Marker (.setMap) and AdvancedMarkerElement (.map = null).
      markersRef.current.forEach((m) => {
        if (typeof m.setMap === 'function') {
          m.setMap(null);
        } else {
          m.map = null;
        }
      });
      markersRef.current = [];
      // Don't tear down the SDK itself — it may be needed if the panel
      // re-mounts (e.g., user navigates away and back). Marker cleanup
      // is sufficient.
    };
  }, []);

  // Error state: parent should be rendering the fallback static map
  // (we already called onLoadError). Render nothing here so we don't
  // double-display.
  if (status === 'error') {
    return null;
  }

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
      />
      {status === 'loading' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-secondary)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-sm)',
            pointerEvents: 'none',
          }}
        >
          Loading map…
        </div>
      )}
    </div>
  );
}
